<template>
  <v-dialog :value="value" max-width="440" persistent>
    <v-card dark class="wcd-card rounded-xl">
      <div class="wcd-header">
        <div class="d-flex align-center" style="gap: 10px">
          <div class="wcd-icon" :style="{ background: data?.template.color || '#4CAF50' }">
            <v-icon small color="white">{{ data?.template.icon || "mdi-calendar" }}</v-icon>
          </div>
          <div>
            <div class="wcd-title">{{ data?.template.title }}</div>
            <div class="wcd-subtitle">{{ selectedEmployeeName }}</div>
          </div>
        </div>
        <v-btn icon x-small @click="$emit('input', false)" style="color: rgba(255, 255, 255, 0.3)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pt-4 pb-2 overflow-visible">
        <div class="wcd-time-label mb-1">Mitarbeiter</div>
        <v-select
          v-model="localColId"
          :items="employees"
          item-text="name"
          item-value="id"
          dense
          solo
          flat
          dark
          hide-details
          background-color="rgba(255,255,255,0.04)"
          class="wcd-time-input mb-4"></v-select>

        <div class="wcd-time-row">
          <div class="wcd-time-item">
            <div class="wcd-time-label">Start</div>
            <v-text-field
              v-if="data"
              v-model="localStart"
              type="time"
              dense
              solo
              flat
              dark
              hide-details
              background-color="rgba(255,255,255,0.04)"
              class="wcd-time-input"></v-text-field>
          </div>
          <div class="wcd-time-item" v-if="data">
            <div class="wcd-time-label">Dauer</div>
            <v-text-field
              v-model.number="localDuration"
              type="number"
              suffix="Min"
              dense
              solo
              flat
              dark
              hide-details
              background-color="rgba(255,255,255,0.04)"
              class="wcd-time-input"></v-text-field>
          </div>
          <div class="wcd-time-item">
            <div class="wcd-time-label">Ende</div>
            <div class="wcd-time-result">{{ calculatedEnd }}</div>
          </div>
        </div>

        <!-- Kunde Bereich (nur bei requiresCustomer) -->
        <div v-if="data?.template.requiresCustomer" class="mt-4 relative" style="z-index: 1000">
          <div class="wcd-time-label mb-1">Kunde *</div>

          <!-- Toggle: Account vorhanden / kein Account -->
          <div class="d-flex mb-3" style="gap: 4px">
            <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode }" @click="noAccountMode = false">
              <v-icon x-small class="mr-1">mdi-account-search</v-icon> Kunde suchen
            </div>
            <div
              class="wcd-toggle-btn flex-grow-1"
              :class="{ active: noAccountMode }"
              @click="
                noAccountMode = true
                selectedCustomer = null
              ">
              <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
            </div>
          </div>

          <!-- Modus 1: Normaler Kunden-Suche -->
          <template v-if="!noAccountMode">
            <user-search
              :value="selectedCustomer"
              @update="updateCustomer"
              return-object
              ignore-rights
              label="Kunden suchen..."
              dark
              outlined
              dense
              :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
          </template>

          <!-- Modus 2: Kein Account – Manuelle Eingabe -->
          <template v-else>
            <div class="d-flex gap-2">
              <v-text-field v-model="noAccountInfo.firstName" label="Vorname *" outlined dense dark hide-details></v-text-field>
              <v-text-field v-model="noAccountInfo.lastName" label="Nachname *" outlined dense dark hide-details></v-text-field>
            </div>
            <v-text-field
              v-model="noAccountInfo.phone"
              label="Telefon *"
              outlined
              dense
              dark
              hide-details
              class="mt-2"
              prepend-inner-icon="mdi-phone"></v-text-field>
            <v-text-field
              v-model="noAccountInfo.email"
              label="E-Mail (Optional)"
              outlined
              dense
              dark
              hide-details
              class="mt-2"
              prepend-inner-icon="mdi-email-outline"></v-text-field>
          </template>
        </div>

        <!-- Zweite Person hinzufügen (nur bei requiresCustomer) -->
        <div v-if="data?.template.requiresCustomer && (selectedCustomer || noAccountMode)" class="mt-3">
          <div v-if="!showSecondPerson" class="d-flex align-center">
            <v-btn text x-small color="blue lighten-2" @click="addSecondPerson" class="text-none">
              <v-icon left x-small>mdi-account-plus</v-icon> Zweite Person hinzufügen (+30 Min)
            </v-btn>
          </div>
          <div v-else>
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="wcd-time-label">2. Person</div>
              <v-btn icon x-small @click="removeSecondPerson" style="color: rgba(255, 255, 255, 0.3)">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>

            <div class="d-flex mb-2" style="gap: 4px">
              <div class="wcd-toggle-btn flex-grow-1" :class="{ active: !noAccountMode2 }" @click="noAccountMode2 = false">
                <v-icon x-small class="mr-1">mdi-account-search</v-icon> Suchen
              </div>
              <div
                class="wcd-toggle-btn flex-grow-1"
                :class="{ active: noAccountMode2 }"
                @click="
                  noAccountMode2 = true
                  selectedCustomer2 = null
                ">
                <v-icon x-small class="mr-1">mdi-account-off</v-icon> Kein Account
              </div>
            </div>

            <template v-if="!noAccountMode2">
              <user-search
                :value="selectedCustomer2"
                @update="updateCustomer2"
                return-object
                ignore-rights
                label="2. Kunden suchen..."
                dark
                outlined
                dense
                :menu-props="{ zIndex: 9999, offsetY: true }"></user-search>
            </template>
            <template v-else>
              <div class="d-flex gap-2">
                <v-text-field v-model="noAccountInfo2.firstName" label="Vorname *" outlined dense dark hide-details></v-text-field>
                <v-text-field v-model="noAccountInfo2.lastName" label="Nachname *" outlined dense dark hide-details></v-text-field>
              </div>
              <v-text-field
                v-model="noAccountInfo2.phone"
                label="Telefon *"
                outlined
                dense
                dark
                hide-details
                class="mt-2"
                prepend-inner-icon="mdi-phone"></v-text-field>
              <v-text-field
                v-model="noAccountInfo2.email"
                label="E-Mail (Optional)"
                outlined
                dense
                dark
                hide-details
                class="mt-2"
                prepend-inner-icon="mdi-email-outline"></v-text-field>
            </template>

            <div class="wcd-dual-badge mt-2">
              <v-icon x-small color="blue lighten-2" class="mr-1">mdi-account-multiple</v-icon>
              <span>Dauer +30 Min ({{ localDuration }} Min total)</span>
            </div>
          </div>
        </div>

        <div v-if="data?.template.specialType === 'new_contract'" class="mt-4 d-flex flex-column gap-2">
          <div class="wcd-time-label mb-1">Neuer Kunde (Gast)</div>
          <div class="d-flex gap-2">
            <v-text-field v-model="guestData.firstName" label="Vorname *" outlined dense dark hide-details></v-text-field>
            <v-text-field v-model="guestData.lastName" label="Nachname *" outlined dense dark hide-details></v-text-field>
          </div>
          <v-text-field v-model="guestData.email" label="E-Mail (Optional)" outlined dense dark hide-details class="mt-2"></v-text-field>
        </div>

        <v-textarea
          v-if="data"
          v-model="localDesc"
          label="Notiz (Optional)"
          outlined
          dense
          rows="2"
          dark
          hide-details
          class="mt-4"
          style="font-size: 0.85rem"></v-textarea>
      </v-card-text>

      <v-card-actions class="px-5 pb-4 pt-2">
        <v-spacer></v-spacer>
        <v-btn text small @click="$emit('input', false)" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Abbrechen</v-btn>
        <v-btn small depressed class="wcd-save-btn" @click="save" :disabled="!canSave"> <v-icon left x-small>mdi-check</v-icon> Erstellen </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UserSearch from "@/components/userSearch"
import moment from "moment"

export default {
  name: "WorkCalendarDropDialog",
  components: { UserSearch },
  props: ["value", "data", "employees"], // Neu: employees Prop hinzugefügt
  data() {
    return {
      localColId: null, // Neu: Hält die ID des gewählten Mitarbeiters
      localStart: "",
      localDuration: 60,
      localDesc: "",
      selectedCustomer: null,
      guestData: {
        firstName: "",
        lastName: "",
        email: "",
      },
      // Kein Account Modus
      noAccountMode: false,
      noAccountInfo: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      // Zweite Person
      showSecondPerson: false,
      selectedCustomer2: null,
      noAccountMode2: false,
      noAccountInfo2: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      baseDuration: 60, // Speichert die Basis-Dauer ohne +30
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val) {
          // Initialisiert die Auswahl mit der Spalte, auf die geklickt/gedroppt wurde
          this.localColId = val.col?.id || null
          this.localStart = val.time
          this.baseDuration = val.template.duration || 60
          this.localDuration = this.baseDuration
          this.localDesc = val.description || ""
          this.selectedCustomer = null
          this.selectedCustomer2 = null
          this.noAccountMode = false
          this.noAccountMode2 = false
          this.showSecondPerson = false
          this.noAccountInfo = { firstName: "", lastName: "", phone: "", email: "" }
          this.noAccountInfo2 = { firstName: "", lastName: "", phone: "", email: "" }
          this.guestData = { firstName: "", lastName: "", email: "" }
        }
      },
    },
  },
  computed: {
    calculatedEnd() {
      if (!this.localStart) return "-"
      return moment(this.localStart, "HH:mm").add(this.localDuration, "minutes").format("HH:mm")
    },
    // Ermittelt den Anzeigenamen für den Header
    selectedEmployeeName() {
      if (!this.employees || !this.localColId) return this.data?.col?.firstName || "Mitarbeiter"
      const emp = this.employees.find((e) => e.id === this.localColId)
      return emp ? emp.name : this.data?.col?.firstName || "Mitarbeiter"
    },
    canSave() {
      // Prüfe requiresCustomer Bedingung
      if (this.data?.template.requiresCustomer) {
        // Mindestens Kunde 1 muss vorhanden sein
        const hasCustomer1 = this.noAccountMode
          ? this.noAccountInfo.firstName && this.noAccountInfo.lastName && this.noAccountInfo.phone
          : !!this.selectedCustomer
        if (!hasCustomer1) return false

        // Wenn zweite Person aktiv, auch diese prüfen
        if (this.showSecondPerson) {
          const hasCustomer2 = this.noAccountMode2
            ? this.noAccountInfo2.firstName && this.noAccountInfo2.lastName && this.noAccountInfo2.phone
            : !!this.selectedCustomer2
          if (!hasCustomer2) return false
        }
      }

      // Prüfe new_contract Gast-Daten
      if (this.data?.template.specialType === "new_contract" && (!this.guestData.firstName || !this.guestData.lastName)) {
        return false
      }

      return true
    },
  },
  methods: {
    updateCustomer(value) {
      this.selectedCustomer = value
    },
    updateCustomer2(value) {
      this.selectedCustomer2 = value
    },
    addSecondPerson() {
      this.showSecondPerson = true
      this.localDuration = this.baseDuration + 30
    },
    removeSecondPerson() {
      this.showSecondPerson = false
      this.selectedCustomer2 = null
      this.noAccountMode2 = false
      this.noAccountInfo2 = { firstName: "", lastName: "", phone: "", email: "" }
      this.localDuration = this.baseDuration
    },
    save() {
      const payload = {
        ...this.data,
        colId: this.localColId, // Übergibt die gewählte Mitarbeiter ID
        time: this.localStart,
        duration: this.localDuration,
        description: this.localDesc,
        customer: this.noAccountMode ? null : this.selectedCustomer,
        noAccountData: this.noAccountMode ? { ...this.noAccountInfo } : null,
        guestData: this.data?.template.specialType === "new_contract" ? { ...this.guestData } : null,
        // Zweite Person
        customer2: this.showSecondPerson && !this.noAccountMode2 ? this.selectedCustomer2 : null,
        noAccountData2: this.showSecondPerson && this.noAccountMode2 ? { ...this.noAccountInfo2 } : null,
        hasSecondPerson: this.showSecondPerson,
      }
      this.$emit("save", payload)
    },
  },
}
</script>

<style scoped>
.wcd-card {
  background: rgba(22, 22, 22, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.wcd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
}
.wcd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wcd-title {
  font-size: 1rem;
  font-weight: 700;
  color: white;
}
.wcd-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
}

/* Time Row */
.wcd-time-row {
  display: flex;
  gap: 10px;
}
.wcd-time-item {
  flex: 1;
}
.wcd-time-label {
  font-size: 0.6rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.wcd-time-input >>> input {
  text-align: center;
  font-weight: 700;
  font-size: 0.85rem;
}
.wcd-time-input >>> .v-input__slot {
  border-radius: 8px !important;
  min-height: 36px !important;
}
.wcd-time-result {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: #66bb6a;
  background: rgba(76, 175, 80, 0.08);
  border-radius: 8px;
}

/* Toggle Buttons */
.wcd-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all 0.2s;
  text-transform: none;
}
.wcd-toggle-btn.active {
  color: white;
  background: rgba(66, 165, 245, 0.15);
  border-color: rgba(66, 165, 245, 0.4);
}
.wcd-toggle-btn:hover {
  border-color: rgba(255, 255, 255, 0.15);
}
.wcd-toggle-btn i {
  color: inherit !important;
}

/* Dual Person Badge */
.wcd-dual-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #64b5f6;
  background: rgba(66, 165, 245, 0.08);
  border: 1px solid rgba(66, 165, 245, 0.15);
}

/* Save Button */
.wcd-save-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
}
.overflow-visible {
  overflow: visible !important;
}
.gap-2 {
  gap: 8px;
}
</style>
